import { SceneData } from './SceneExporter';
import * as THREE from 'three';
import JSZip from 'jszip';
import OBJExporter from './ObjExporter/OBJExporter.js';
import { RepaintProgress } from '../Gui/components/panels/ProgressBar';

/**
 * Export3DObj.ts
 *
 * Goal:
 * - Export SceneData to OBJ + MTL.
 *
 * Rule:
 * - Meshes without geometry.position crash the javascript exporter, therefore the code filters them out.
 */

export async function Export3DObj(
    scenedata: SceneData,
    filename: string,
    setProgress: (progress: number | undefined) => void
): Promise<{ formattedData: Uint8Array; extension?: string }> {
    console.log('Exporting scene to OBJ format');

    const exporter = new OBJExporter();
    const sceneToExport: THREE.Scene = new THREE.Scene();

    sceneToExport.name = scenedata.geoDataName || 'ExportedScene';

    let progress = 0;
    const totalProgress = Math.max(scenedata.layerMeshes.length, 1);
    setProgress((progress / totalProgress) * 80 + 10);

    // Filter out meshes without geometry.position to prevent exporter crashes
    const isValidMeshForObj = (obj: THREE.Object3D): obj is THREE.Mesh => {
        const geom = (obj as any).geometry as THREE.BufferGeometry | undefined;
        if (!geom || geom.isBufferGeometry !== true) return false;

        const pos = geom.getAttribute?.('position') as THREE.BufferAttribute | undefined;
        return !!pos && typeof pos.count === 'number' && pos.count > 0;
    };

    // Rebuild the scene from the scenedata
    for (let i = 0; i < scenedata.layerMeshes.length; i++) {
        const group = new THREE.Group();
        group.name = scenedata.layerGroupNames[i] || `Group_${i}`;

        for (const root of scenedata.layerMeshes[i]) {
            root.traverse((obj) => {
                if (!isValidMeshForObj(obj)) return;

                const mesh = obj as THREE.Mesh;
                const cloned = mesh.clone(false) as THREE.Mesh;
                cloned.geometry = (mesh.geometry as THREE.BufferGeometry).clone();

                cloned.position.set(0, 0, 0);
                cloned.rotation.set(0, 0, 0);
                cloned.scale.set(1, 1, 1);
                cloned.updateMatrix();
                cloned.updateMatrixWorld(true);

                group.add(cloned);
            });
        }

        sceneToExport.add(group);

        progress++;
        setProgress((progress / totalProgress) * 80 + 10);
        await RepaintProgress();
    }

    // obj files need the material .mtl file to keep surface colors
    const { obj: objectData, mtl: materialData } = exporter.parse(sceneToExport);

    // Package as ZIP
    const zip = new JSZip();
    zip.file(`${filename}.obj`, objectData);
    if (materialData) {
        zip.file(`${filename}.mtl`, materialData);
    }

    const zippedData = await zip.generateAsync({ type: 'uint8array' });

    return { formattedData: zippedData, extension: 'zip' };
}
